import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { cartRevalidate } from '../../util/api';
import { getCartRows } from '../../util/cart';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { loadShippingDestination } from '../../util/shippingDestination';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  listingCardImageFields,
  listingCardImageVariantParams,
} from '../../util/listingImageVariants';

import { fetchCurrentUser } from '../../ducks/user.duck';

const { UUID } = sdkTypes;

const EMPTY_RESULT = { rows: [], groups: [] };

const cartHasShippingRows = rows =>
  (Array.isArray(rows) ? rows : []).some(row => row.deliveryMethod === 'shipping');

const payloadRows = payload => (Array.isArray(payload) ? payload : payload?.rows) || [];

const payloadDestination = payload => (Array.isArray(payload) ? undefined : payload?.destination);

/** Sticky dest from explicit payload or localStorage (client-only). */
const resolveQuoteDestination = (rows, destination) => {
  if (destination?.country) {
    return destination;
  }
  if (cartHasShippingRows(rows) && typeof window !== 'undefined') {
    return loadShippingDestination();
  }
  return null;
};

// ================ Async Thunks ================ //

/**
 * Ask the server for live listing data and the shipping ledger of every box.
 *
 * The cart persists five fields per row, so everything this page renders — title, image,
 * live price, stock, fees, seller, and all money — comes from here. An empty cart needs
 * no request: the answer is already known.
 */
const revalidateCartThunk = createAsyncThunk(
  'CartPage/revalidateCart',
  (payload, { rejectWithValue }) => {
    const requested = payloadRows(payload);
    if (requested.length === 0) {
      return Promise.resolve(EMPTY_RESULT);
    }
    const resolvedDestination = resolveQuoteDestination(requested, payloadDestination(payload));
    const quoteRows = requested.map(({ listingId, qty, deliveryMethod, unitPriceAtAdd }) => ({
      listingId,
      qty,
      deliveryMethod,
      unitPriceAtAdd,
    }));
    return (resolvedDestination
      ? cartRevalidate(quoteRows, { destination: resolvedDestination })
      : cartRevalidate(quoteRows)
    ).catch(e => rejectWithValue(storableError(e)));
  }
);

// ================ Slice ================ //

const initialState = {
  revalidateInProgress: false,
  revalidateError: null,
  latestRevalidateRequestId: null,
  // Server-stamped rows, in cart order. Rows are the truth about emptiness; groups are not.
  rows: [],
  groups: [],
  loaded: false,
};

const cartPageSlice = createSlice({
  name: 'CartPage',
  initialState,
  reducers: {
    clearCartPage: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(revalidateCartThunk.pending, (state, action) => {
        state.revalidateInProgress = true;
        state.revalidateError = null;
        state.latestRevalidateRequestId = action.meta.requestId;
      })
      .addCase(revalidateCartThunk.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestRevalidateRequestId) {
          return;
        }
        state.revalidateInProgress = false;
        state.loaded = true;
        state.rows = action.payload?.rows || [];
        state.groups = action.payload?.groups || [];
      })
      .addCase(revalidateCartThunk.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestRevalidateRequestId) {
          return;
        }
        state.revalidateInProgress = false;
        state.loaded = true;
        state.revalidateError = action.payload || null;
      });
  },
});

export const { clearCartPage } = cartPageSlice.actions;

export default cartPageSlice.reducer;

// ================ Thunk Wrappers ================ //

/**
 * @param {Array<Object>} rows persisted cart rows
 * @param {{ destination?: { country?: string, state?: string } }} [options]
 */
export const revalidateCart = (rows, options = {}) => dispatch =>
  dispatch(
    revalidateCartThunk({
      rows,
      ...(options?.destination ? { destination: options.destination } : {}),
    })
  );

/**
 * CheckoutPage's ResponsiveImage only paints variants whose names start with
 * `listing-card`. Sharetribe returns those only when the show call asks for them.
 *
 * @param {Object|null|undefined} listingImageConfig config.layout.listingImage
 * @returns {Object} listings.show image fields
 */
const checkoutListingImageParams = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    listingImageConfig || {};
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      ...listingCardImageFields(variantPrefix),
    ],
    ...listingCardImageVariantParams(variantPrefix, aspectRatio),
  };
};

/**
 * CheckoutPage needs a denormalised listing (author, price, process alias, photos).
 * The cart quote does not carry that entity.
 *
 * @param {string} listingId
 * @param {Object} [config] merged marketplace config; listingImage layout is used for variants
 * @returns {Promise<Object|null>}
 */
export const fetchListingForCheckout = (listingId, config) => (dispatch, getState, sdk) => {
  return sdk.listings
    .show({
      id: new UUID(listingId),
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      ...checkoutListingImageParams(config?.layout?.listingImage),
    })
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      return entities[0] || null;
    });
};

// ================ Load data ================ //

/**
 * A guest's cart lives in localStorage, which the server cannot read, so SSR can only
 * quote an account cart. Shipping rows always defer quoting to CartPage after sticky
 * destination hydrates — loadData must not fire a default-zone quote that can race
 * the client's destination-aware request.
 */
export const loadData = () => (dispatch, getState) => {
  return dispatch(fetchCurrentUser()).then(() => {
    const currentUser = getState().user?.currentUser;
    const rows = currentUser ? getCartRows(currentUser) : [];
    if (cartHasShippingRows(rows)) {
      return Promise.resolve();
    }
    return dispatch(revalidateCart(rows));
  });
};
